import { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import API from 'api/API';
import { DraftSatoSetResponse, TrayOnboardingStatus } from 'api/Responses/SatoResponses';
import { DraftTrayResponse } from 'api/Responses/TrayResponses';
import { useLanguage } from 'context/AppContext';
import { SelectedSetSelectors } from 'context/SelectedSet';
import { useSelectedSet } from 'context/SelectedSet/SelectedSetContext';
import { getSetData, getTrayData } from 'utils/helpers/SATO.helpers';

interface HookReturn {
  loading: boolean;
  set: DraftSatoSetResponse;
  tray: DraftTrayResponse;
  reviewTray: (
    status: TrayOnboardingStatus.MODEL_CONFIG | TrayOnboardingStatus.SKIPPED | TrayOnboardingStatus.REJECTED,
    comment?: string,
  ) => Promise<void>;
}

const useReviewTray = (drafts: DraftSatoSetResponse[], setCode: string, trayIndex: number): HookReturn | undefined => {
  const [language] = useLanguage();
  const { selectedSetState, dispatchSelectedSet } = useSelectedSet();
  const [loading, setLoading] = useState(false);

  /**
   * Used more specifically for on page refresh when no set is declared in the reducer state.
   * This will compile the data from drafts into specific sets and trays.
   */
  useEffect(() => {
    if (!selectedSetState.selectedSet && selectedSetState.selectedTrayIndex !== undefined) {
      const draftSet = getSetData(drafts, setCode);
      const draftTray = getTrayData(drafts, setCode, trayIndex);

      if (draftSet && draftTray) {
        dispatchSelectedSet({
          type: 'SET_SELECTED_SET',
          payload: draftSet,
        });

        dispatchSelectedSet({
          type: 'SET_SELECTED_TRAY',
          payload: trayIndex,
        });
      }
    }
  }, [selectedSetState, drafts, setCode, dispatchSelectedSet, trayIndex]);

  const set = useMemo(
    () => (selectedSetState.selectedSet ? SelectedSetSelectors.selectSelectedSet(selectedSetState) : undefined),
    [selectedSetState],
  );

  const tray = useMemo(
    () =>
      selectedSetState.selectedTrayIndex != null
        ? SelectedSetSelectors.selectSelectedTray(selectedSetState)
        : undefined,
    [selectedSetState],
  );

  // Check if exists
  if (!set || !tray) {
    return;
  }

  const reviewTray = async (
    status: TrayOnboardingStatus.MODEL_CONFIG | TrayOnboardingStatus.SKIPPED | TrayOnboardingStatus.REJECTED,
    comment?: string,
  ): Promise<void> => {
    setLoading(true);

    try {
      if (status === TrayOnboardingStatus.REJECTED && !comment)
        throw new Error(language.Sato.videosPage.trayRejectWithoutComment);
      await API.sato.updateTrayStatus(set.code, tray.tray_id, status, comment);

      dispatchSelectedSet({
        type: 'REVIEW_TRAY',
        payload: { status, comment },
      });

      toast.success(language.Sato.videosPage.trayReviewCompleted[status]);
    } catch (error) {
      API.errorHandler(error);
    }

    setLoading(false);
  };

  return {
    set,
    tray,
    loading,
    reviewTray,
  };
};

export default useReviewTray;
